import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  filterFns,
  type ColumnDef,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useAdminApiQuery } from '@/hooks/use-admin-api-query';
import { qk } from '@/lib/query/keys';
import { Input } from '@/components/ui/input';
import { StatCards, type Stats } from '@/components/super-admin/stat-cards';
import { DataTable } from '@/components/super-admin/data-table';
import { PageContainer } from '@/components/page-container';
import { unitLabel } from '@/lib/usage-unit';

interface WorkspaceRow {
  id: string;
  name: string;
  planSlug: string;
  memberCount: number;
  channelCount: number;
  usage: {
    /** Usage this quota period, in `usageUnit` (AIT-436). */
    used: number;
    usageUnit: 'messages' | 'actions';
    orgPoolLimit: number;
    orgPoolSharePercentage: number;
  };
}

export function SuperAdminWorkspacesPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const { data: stats = null, isLoading: statsLoading } = useAdminApiQuery<Stats>(
    qk.superAdmin('channels', 'stats'),
    '/super-admin/channels/stats',
  );
  const { data, isLoading } = useAdminApiQuery<WorkspaceRow[]>(
    qk.superAdmin('workspaces'),
    '/super-admin/workspaces',
  );
  // A `= []` default mints a new array every render, which remounts every cell.
  const rows = useMemo(() => data ?? [], [data]);

  const columns = useMemo<ColumnDef<WorkspaceRow>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        enableSorting: true,
        meta: { cellClassName: 'font-medium', sortLabel: 'name' },
      },
      {
        accessorKey: 'planSlug',
        header: 'Plan',
        enableSorting: true,
        meta: { headerClassName: 'w-[120px]', cellClassName: 'capitalize', sortLabel: 'plan' },
      },
      {
        accessorKey: 'memberCount',
        header: 'Members',
        enableSorting: true,
        enableGlobalFilter: false,
        meta: { headerClassName: 'w-[100px]', sortLabel: 'members' },
      },
      {
        accessorKey: 'channelCount',
        header: 'Channels',
        enableSorting: true,
        enableGlobalFilter: false,
        meta: { headerClassName: 'w-[100px]', sortLabel: 'channels' },
      },
      {
        id: 'orgPoolShare',
        // Accessor added so the column has a primitive value to sort by; it
        // previously had only an id and a renderer.
        accessorFn: (row) => row.usage.orgPoolSharePercentage,
        header: 'Org Pool Share',
        enableSorting: true,
        enableGlobalFilter: false,
        meta: { headerClassName: 'w-[220px]', sortLabel: 'org pool share' },
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <span className="text-sm tabular-nums">
              {row.original.usage.used.toLocaleString()} /{' '}
              {row.original.usage.orgPoolLimit.toLocaleString()}
            </span>
            <span className="text-xs text-muted-foreground">
              {unitLabel(row.original.usage.usageUnit)}
            </span>
            <div className="h-1.5 w-20 overflow-hidden rounded-full bg-muted">
              <div
                className={`h-full ${row.original.usage.orgPoolSharePercentage >= 100 ? 'bg-destructive' : 'bg-primary'}`}
                style={{ width: `${Math.min(100, row.original.usage.orgPoolSharePercentage)}%` }}
              />
            </div>
          </div>
        ),
      },
    ],
    [],
  );

  const table = useReactTable({
    data: rows,
    columns,
    getRowId: (row) => row.id,
    defaultColumn: { enableSorting: false },
    enableMultiSort: false,
    sortDescFirst: false,
    state: { globalFilter: search },
    // AIT-637: public id (org_/ws_) is not a column, so fold it into the search.
    globalFilterFn: (row, columnId, value, addMeta) =>
      row.original.id.toLowerCase().includes(String(value).toLowerCase()) ||
      filterFns.includesString(row, columnId, value, addMeta),
    onGlobalFilterChange: setSearch,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <PageContainer variant="fluid" className="space-y-6">
      <StatCards stats={stats} isLoading={statsLoading} />
      <Input
        placeholder="Search workspaces by name, plan, or id..."
        aria-label="Search workspaces"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="max-w-sm"
      />
      <div className="rounded-md border">
        <DataTable
          table={table}
          isLoading={isLoading}
          emptyState="No workspaces found."
          onRowActivate={(row) => navigate(`/super-admin/workspaces/${row.original.id}`)}
        />
      </div>
    </PageContainer>
  );
}
